"use client";
import useMediaQuery from "@repo/shared-ui/useMediaQuery";
import React, { useRef, useState, useEffect } from "react";
import "swiper/css";
import "swiper/css/navigation";
import "swiper/css/pagination";
import { Swiper, SwiperSlide } from "swiper/react";
import { A11y, Navigation, Pagination, Scrollbar } from "swiper/modules";
import StockCard from "./StockInfoCard";
import { StocksGridProps } from "./types";
import StockListNotFound from "./StockListNotFound";
import SwiperButton from "@repo/shared-ui/SwiperButton";
import BFLSkeleton from "@repo/shared-ui/BFLSkeleton";
import { dlElementConstants, dlEventConstants } from "@repo/analytics/data-layer.constants";
import { dataLayer } from "@repo/analytics/data-layer-utils";

export const StocksGrid: React.FC<StocksGridProps> = ({
  stocks,
  defaultImage,
  errorMessage,
  stockNotFoundImgUrl,
  modalTitle,
  modalBtnCta,
  successMsg,
  errorMsg,
  onCardClick,
  section_title,
  cta_text
}) => {
  const isMobile = useMediaQuery("(max-width: 1024px)");

  const prevRef = useRef(null);
  const nextRef = useRef(null);
  const [end, setEnd] = useState(false);
  const [start, setStart] = useState(true);

  const [mounted, setMounted] = useState(false);
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleNext = () => {
    const data = {
      section_title: section_title,
      component: section_title,
      event: dlEventConstants.CLICK,
      element: dlElementConstants.VIEW_ALL_CLICK,
      cta_text: cta_text,
    };
    const cleverTapObj = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    };
    dataLayer("", data, cleverTapObj);
  };

  const handlePrev = () => {
    const dataLayerObj = {
      section_title: section_title,
      component: section_title,
      event: dlEventConstants.CLICK,
      element: dlElementConstants.VIEW_ALL_CLICK,
      cta_text: cta_text,
    };
    const cleverTapObject = {
      EP_PAGE_NAME: dlElementConstants.TRADING_MALL_CLP,
      EP_JOURNEY_NAME: dlElementConstants.TRADING_MALL_CLP_EP,
    };
    dataLayer("", dataLayerObj, cleverTapObject);
  };

  let content;
  if (!stocks || stocks?.length <= 0) {
    content = (
      <StockListNotFound
        errorMessage={errorMessage || ""}
        stockNotFoundImgUrl={stockNotFoundImgUrl || ""}
      />
    )
  } else if (isMobile) {
    content = (
      <div className="grid grid-cols-2 gap-3">
        {stocks.slice(0, 4).map((stock, index: number) => (
          <StockCard
            stock={stock}
            key={"StockGrid-" + index}
            fullWidth
            modalTitle={modalTitle}
            modalBtnCta={modalBtnCta}
            successMsg={successMsg}
            errorMsg={errorMsg}
            onCardClick={onCardClick}
            cta_text={cta_text}
            section_title={section_title}
          />
        ))}
      </div>
    )
  } else {
    content = (
      <div className="relative">
        <SwiperButton ref={prevRef} direction="prev" isDisabled={start} onClick={handlePrev} />
        <SwiperButton ref={nextRef} direction="next" isDisabled={end} onClick={handleNext} />

        <Swiper
          modules={[Navigation, Pagination, Scrollbar, A11y]}
          spaceBetween={0}
          slidesPerView="auto"
          allowTouchMove={false}
          observer={true}
          observeParents={true}
          navigation={{
            prevEl: prevRef.current,
            nextEl: nextRef.current,
          }}
          onBeforeInit={(swiper: any) => {
            if (typeof swiper.params.navigation !== "boolean") {
              swiper.params.navigation.prevEl = prevRef.current;
              swiper.params.navigation.nextEl = nextRef.current;
            }
          }}
          onInit={(swiper: any) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onSlideChange={(swiper: any) => {
            setStart(swiper.isBeginning);
            setEnd(swiper.isEnd);
          }}
          onReachEnd={() => setEnd(true)}
          onFromEdge={() => setEnd(false)}
          scrollbar={{ draggable: false }}
        >
          {stocks.map((stock, index: number) => (
            <SwiperSlide
              key={"StockGrid-" + index}
              className={`min-w-[214px]! max-w-[214px] ${index === stocks.length - 1 ? "mr-0!" : "mr-3!"
                }`}
            >
              <StockCard
                stock={stock}
                defaultImage={defaultImage}
                modalTitle={modalTitle}
                modalBtnCta={modalBtnCta}
                successMsg={successMsg}
                errorMsg={errorMsg}
                onCardClick={onCardClick}
                cta_text={cta_text}
                section_title={section_title}
              />
            </SwiperSlide>
          ))}
        </Swiper>
      </div>

    )
  }
  return (
    <div className="relative">
      {!mounted && (
        <div className="absolute inset-0 z-0">
          <BFLSkeleton height={169} />
        </div>
      )}
      <div className={mounted ? "" : "opacity-0"} >
        {content}
      </div>
    </div>
  );
};
